import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi } from '../api';
import { useToast } from '../../../components/ui/Toast';
import { GOV_TYPE, GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';

type FilterTab = 'ALL' | 'UPCOMING' | 'PAST' | 'CANCELLED';

const STATUS_STYLES: Record<string, { bg: string; color: string; label: string }> = {
  CONFIRMED: { bg: `${T.success}14`, color: T.success, label: 'CONFIRMED' },
  SCHEDULED: { bg: `${T.info}14`, color: T.info, label: 'SCHEDULED' },
  PENDING: { bg: `${T.warning}14`, color: T.warning, label: 'PENDING' },
  COMPLETED: { bg: `${T.teal}14`, color: T.teal, label: 'COMPLETED' },
  MISSED: { bg: `${T.danger}14`, color: T.danger, label: 'MISSED' },
  NO_SHOW: { bg: `${T.danger}14`, color: T.danger, label: 'NO SHOW' },
  CANCELLED: { bg: `${T.danger}14`, color: T.danger, label: 'CANCELLED' },
};

interface Appointment {
  id: string;
  doctorName: string;
  specialty: string;
  facility: string;
  nhcDoctorId: string;
  nhcFacilityId: string;
  appointmentId: string;
  date: string;
  time: string;
  type: 'OPD' | 'Telemedicine' | 'Lab' | 'Follow-Up';
  status: string;
  reason?: string;
  notes?: string;
}

type ApiAppointment = Awaited<ReturnType<typeof patientApi.getAppointments>>[number];

function toLocalAppointment(a: ApiAppointment): Appointment {
  const appointedAt = a.appointedAt ? new Date(a.appointedAt) : null;
  const isVideo = a.consultationType === 'VIDEO';
  return {
    id: a.id,
    doctorName: a.doctorName || 'Doctor',
    specialty: a.speciality || '',
    facility: a.hospitalName || (isVideo ? 'Video Consultation' : ''),
    nhcDoctorId: '',
    nhcFacilityId: '',
    appointmentId: a.appointmentId,
    date: appointedAt ? appointedAt.toISOString() : '',
    time: appointedAt ? appointedAt.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }) : '',
    type: isVideo ? 'Telemedicine' : 'OPD',
    status: a.status,
    reason: a.reasonForVisit ?? undefined,
  };
}

function fmtDate(d: string): string {
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return d; }
}

function TypeIcon({ type }: { type: string }) {
  const color = type === 'Telemedicine' ? T.purple : type === 'Lab' ? T.teal : T.navy;
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {type === 'Telemedicine' ? (
        <><rect x="2" y="3" width="20" height="13" rx="2" /><path d="M8 21h8M12 17v4" /><circle cx="12" cy="10" r="3" /></>
      ) : type === 'Lab' ? (
        <><path d="M9 3h6M10 3v6l-4 9h12l-4-9V3" /><circle cx="9" cy="15" r="1" fill={color} stroke="none" /></>
      ) : (
        <><circle cx="12" cy="8" r="4" /><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /></>
      )}
    </svg>
  );
}

export default function FollowUpReminders() {
  const navigate = useNavigate();
  const toast = useToast();
  const [apiAppointments, setApiAppointments] = useState<ApiAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState<FilterTab>('ALL');
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    patientApi.getAppointments()
      .then(data => { if (alive) { setApiAppointments(data); setLoadError(false); } })
      .catch(() => { if (alive) setLoadError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const handleCancel = useCallback(async (apptId: string) => {
    setCancellingId(apptId);
    try {
      const updated = await patientApi.cancelAppointment(apptId);
      setApiAppointments(prev => prev.map(a => (a.id === apptId ? updated : a)));
      toast.success('Appointment cancelled');
    } catch (err: any) {
      toast.error(err?.message || 'Could not cancel appointment.');
    } finally {
      setCancellingId(null);
    }
  }, [toast]);

  const appointments: Appointment[] = apiAppointments.map(toLocalAppointment);

  const filtered = appointments.filter(a => {
    if (activeTab === 'ALL') return true;
    if (activeTab === 'CANCELLED') return a.status === 'CANCELLED';
    const isPast = new Date(a.date) < new Date();
    if (activeTab === 'UPCOMING') return !isPast && a.status !== 'CANCELLED';
    if (activeTab === 'PAST') return isPast || a.status === 'COMPLETED';
    return true;
  });

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'ALL', label: 'All' },
    { key: 'UPCOMING', label: 'Upcoming' },
    { key: 'PAST', label: 'Past' },
    { key: 'CANCELLED', label: 'Cancelled' },
  ];

  return (
    <div style={{ ...govPageContainer() }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, paddingTop: 4 }}>
        <div>
          <h1 style={{ ...GOV_TYPE.pageTitle }}>Appointments</h1>
          <p style={{ fontSize: 11, color: T.textSec, marginTop: 4 }}>Track your appointments, follow-ups, and reminders</p>
        </div>
        <button
          onClick={() => navigate('/patient/care-services/doctors')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: T.teal, color: T.white, border: 'none', borderRadius: 10,
            padding: '8px 16px', fontSize: 11, fontWeight: 700, fontFamily: T.font, cursor: 'pointer',
          }}
        >
          + Book Appointment
        </button>
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: 0, background: T.ice, borderRadius: 10, padding: 3, marginBottom: 20 }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              flex: 1, padding: '8px 0', fontSize: 11, fontWeight: activeTab === t.key ? 700 : 500,
              color: activeTab === t.key ? T.white : T.textSec,
              background: activeTab === t.key ? T.teal : 'transparent',
              border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: T.font,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Load Error Banner */}
      {loadError && !loading && (
        <div style={{
          background: `${T.danger}08`, border: `1px solid ${T.danger}20`, borderRadius: 12,
          padding: 12, marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
          </svg>
          <span style={{ fontSize: 10, color: T.danger, fontFamily: T.font }}>
            Could not load your appointments. Please refresh the page.
          </span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div style={{ fontSize: 11, color: T.textTer }}>Loading appointments...</div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filtered.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '48px 20px', background: T.white,
          border: `1px solid ${T.border}`, borderRadius: 16,
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={T.border} strokeWidth="1.2" strokeLinecap="round" style={{ margin: '0 auto 12px', display: 'block' }}>
            <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M8 2v4M16 2v4M3 10h18" />
          </svg>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 6 }}>No upcoming appointments</div>
          <div style={{ fontSize: 11, color: T.textSec, marginBottom: 20, lineHeight: 1.6 }}>
            Book a doctor, lab test, or telemedicine consultation to get started.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/patient/care-services/doctors')} style={{
              padding: '9px 18px', fontSize: 11, fontWeight: 700, fontFamily: T.font,
              color: T.white, background: T.teal, border: 'none', borderRadius: 10, cursor: 'pointer',
            }}>Book Doctor</button>
            <button onClick={() => navigate('/patient/care-services/labs')} style={{
              padding: '9px 18px', fontSize: 11, fontWeight: 700, fontFamily: T.font,
              color: T.teal, background: T.white, border: `1px solid ${T.teal}`, borderRadius: 10, cursor: 'pointer',
            }}>Book Lab Test</button>
            <button onClick={() => navigate('/patient/care-services/telemedicine')} style={{
              padding: '9px 18px', fontSize: 11, fontWeight: 700, fontFamily: T.font,
              color: T.purple, background: T.white, border: `1px solid ${T.purple}`, borderRadius: 10, cursor: 'pointer',
            }}>Telemedicine</button>
          </div>
        </div>
      )}

      {/* Appointment Cards */}
      {!loading && filtered.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(appt => {
            const s = STATUS_STYLES[appt.status] || STATUS_STYLES.PENDING;
            const isPast = new Date(appt.date) < new Date();
            return (
              <div key={appt.id} style={{
                background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
                padding: 16, boxShadow: T.shadow, opacity: isPast && appt.status !== 'COMPLETED' ? 0.7 : 1,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  {/* Type Icon */}
                  <div style={{
                    width: 42, height: 42, borderRadius: 12,
                    background: appt.type === 'Telemedicine' ? `${T.purple}10` : appt.type === 'Lab' ? `${T.teal}10` : `${T.navy}10`,
                    display: 'grid', placeItems: 'center', flexShrink: 0,
                  }}>
                    <TypeIcon type={appt.type} />
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {appt.doctorName}
                      </div>
                      <span style={{
                        fontSize: 9, fontWeight: 700, color: s.color, background: s.bg,
                        padding: '3px 8px', borderRadius: 6, flexShrink: 0,
                      }}>
                        {s.label}
                      </span>
                    </div>

                    <div style={{ fontSize: 11, color: T.textSec, marginBottom: 2 }}>
                      {appt.specialty}{appt.facility ? ` · ${appt.facility}` : ''}
                    </div>

                    {/* NeoHealthCard IDs */}
                    {appt.nhcDoctorId && (
                      <div style={{ fontSize: 10, color: T.teal, fontFamily: T.mono, fontWeight: 600, marginBottom: 6 }}>
                        {appt.nhcDoctorId}{appt.nhcFacilityId && appt.nhcFacilityId !== appt.nhcDoctorId ? ` · ${appt.nhcFacilityId}` : ''}
                      </div>
                    )}

                    {/* Date + Time + Appointment ID */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: T.textTer }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M8 2v4M16 2v4M3 10h18" /></svg>
                        {fmtDate(appt.date)}
                      </span>
                      {appt.time && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: T.textTer }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                          {appt.time}
                        </span>
                      )}
                      <span style={{ fontSize: 9, color: T.textTer, fontFamily: T.mono }}>
                        {appt.appointmentId}
                      </span>
                    </div>

                    {appt.reason && (
                      <div style={{ fontSize: 10, color: T.textSec, marginTop: 2 }}>{appt.reason}</div>
                    )}

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      {(appt.status === 'CONFIRMED' || appt.status === 'SCHEDULED') && (
                        <button
                          onClick={() => handleCancel(appt.id)}
                          disabled={cancellingId === appt.id}
                          style={{
                            padding: '6px 14px', fontSize: 10, fontWeight: 600, fontFamily: T.font, color: T.danger,
                            background: `${T.danger}08`, border: `1px solid ${T.danger}20`, borderRadius: 8,
                            cursor: cancellingId === appt.id ? 'default' : 'pointer',
                            opacity: cancellingId === appt.id ? 0.6 : 1,
                          }}
                        >
                          {cancellingId === appt.id ? 'Cancelling…' : 'Cancel'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Compliance Footer */}
      <div style={{ ...GOV_COMPLIANCE.text, marginTop: 20 }}>
        NeoHealthCard™ · All appointments are logged · Provider verification via NeoVerify
      </div>
    </div>
  );
}
